import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import {
  formatBeerIngestionMedianReview,
  formatBeerIngestionPilotWindowLabel,
} from '../../utils/brewery/beerIngestionStatus';

function ScoreboardChip({ label, met }) {
  return (
    <Chip
      color={met ? 'success' : 'warning'}
      label={label}
      variant="outlined"
    />
  );
}

ScoreboardChip.propTypes = {
  label: PropTypes.string.isRequired,
  met: PropTypes.bool.isRequired,
};

function formatBarChip(name, bar, suffix = '%') {
  const percent = bar?.percent ?? 0;
  const target = bar?.targetPercent ?? 0;
  return `${name} ${percent}${suffix} (target ${target}${suffix})`;
}

function ScoreboardChips({ scoreboard }) {
  const costTotal = scoreboard.cost?.creditsTotal ?? 0;
  const costBudget = scoreboard.cost?.budgetCredits ?? 0;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <ScoreboardChip
        met={scoreboard.trusted?.met === true}
        label={formatBarChip('Trusted', scoreboard.trusted)}
      />
      <ScoreboardChip
        met={scoreboard.freshness?.met === true}
        label={formatBarChip('Freshness', scoreboard.freshness)}
      />
      <ScoreboardChip
        met={scoreboard.reviewable?.met === true}
        label={formatBarChip('Reviewable', scoreboard.reviewable)}
      />
      <ScoreboardChip
        met={scoreboard.cost?.met === true}
        label={`Cost ${costTotal} / ${costBudget} credits`}
      />
    </Stack>
  );
}

ScoreboardChips.propTypes = {
  scoreboard: PropTypes.object.isRequired,
};

function RepresentationList({ representation }) {
  const openFailures = representation?.openFailures ?? 0;
  const imageMenuCount = representation?.increment1ExceptionsByReason?.image_menu ?? 0;
  const pdfUnreadableCount = representation?.increment1ExceptionsByReason?.pdf_unreadable ?? 0;
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        1b exceptions: {representation?.increment1Exceptions ?? 0}
        {` (image menu ${imageMenuCount}, unreadable PDF ${pdfUnreadableCount})`}
      </Typography>
      <Typography variant="body2">
        Excluded: {representation?.excluded ?? 0}
      </Typography>
      <Typography variant="body2">
        Needs source: {representation?.needsSource ?? 0}
      </Typography>
      <Typography variant="body2">
        <Link component={RouterLink} to="/beer-ingestion?tab=failures">
          {openFailures} open failures
        </Link>
      </Typography>
      <Typography variant="body2">
        Auto-paused: {representation?.autoPaused ?? 0}
      </Typography>
    </Stack>
  );
}

RepresentationList.propTypes = {
  representation: PropTypes.object,
};

function ReviewEffortBlock({ reviewEffort }) {
  const medianLabel = formatBeerIngestionMedianReview(
    reviewEffort?.medianReviewMinutes ?? null,
  );
  const newCandidateCount = reviewEffort?.newCandidateCount ?? 0;
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{medianLabel}</Typography>
      <Typography variant="body2">
        New candidates in reviewed extracts: {newCandidateCount}. Ops: sample 10
        new rows in Review for false-new; not a product sampler.
      </Typography>
    </Stack>
  );
}

ReviewEffortBlock.propTypes = {
  reviewEffort: PropTypes.object,
};

function ApprovalAssertion({ applyRequiresApproval }) {
  if (applyRequiresApproval === false) {
    return (
      <Alert severity="error">
        Apply does not require approval (ITT-891 violated).
      </Alert>
    );
  }
  if (applyRequiresApproval !== true) {
    return (
      <Typography variant="body2">
        Apply approval status unavailable.
      </Typography>
    );
  }
  return (
    <Typography variant="body2">
      Apply requires approval (ITT-891).
    </Typography>
  );
}

ApprovalAssertion.propTypes = {
  applyRequiresApproval: PropTypes.bool,
};

export default function BeerIngestionStatusPanel({
  scoreboard,
  loading,
  error,
}) {
  if (loading) {
    return <CircularProgress aria-label="Loading pilot scoreboard" />;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!scoreboard) {
    return (
      <Alert severity="info">
        Pilot scoreboard is not available yet.
      </Alert>
    );
  }

  const exceptionCount = scoreboard.trusted?.exceptionCount ?? 0;
  const creditsPerBrewery = scoreboard.cost?.creditsPerMonitoredBrewery ?? 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Increment 1 pilot status</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatBeerIngestionPilotWindowLabel()}
        </Typography>
        <ScoreboardChips scoreboard={scoreboard} />
        <Alert severity={scoreboard.trusted?.met ? 'success' : 'warning'} role="status">
          Increment 1 trusted {scoreboard.trusted?.numerator ?? 0}
          {' / '}
          {scoreboard.trusted?.denominator ?? 0}
          {' ('}
          {scoreboard.trusted?.percent ?? 0}
          {'%). 1b exceptions excluded from bar: '}
          {exceptionCount}
          .
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Extract credits per monitored brewery: {creditsPerBrewery}.
          Monitor-check credits are not stored.
        </Typography>
        <RepresentationList representation={scoreboard.representation} />
        <ReviewEffortBlock reviewEffort={scoreboard.reviewEffort} />
        <ApprovalAssertion
          applyRequiresApproval={scoreboard.assertions?.applyRequiresApproval}
        />
      </Stack>
    </Paper>
  );
}

BeerIngestionStatusPanel.propTypes = {
  scoreboard: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};
